// Batch sheet calculator (manufacturer portal). Pick a SKU, type a batch size (BBL / bottles / litres),
// and it scales each recipe ingredient's grams-per-litre up to the whole batch — total grams, lbs and kg
// to weigh out. Mirrors the spirits batch-sheet spreadsheet: BBL → Gallons → Litres → Bottles header,
// then per-ingredient g/L · lbs/BBL · Total. Recipe g/L is the source of truth (volume-independent); when
// a line only has lbs-per-unit stored we recover g/L from the business unit volume the editor used.
import { useEffect, useMemo, useState } from "react";
import type { Conversions, Recipes, Sku } from "@shared/types.ts";
import { skuRecipeKey, gramsPerMassUnit } from "@shared/engine.ts";
import { EmptyState, SegTabs } from "./ui.tsx";

type Mode = "bbl" | "bottles" | "litres";

const fx = (n: number, dp: number) =>
  (Number.isFinite(n) ? n : 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });

export function BatchSheet({ skus, recipes, conv, massUnit, unitLabel, businessUnitVolumeL, onSaveNotes, onToggleLine }: {
  skus: Sku[];
  recipes: Recipes;
  conv: Conversions;
  massUnit?: string;
  unitLabel?: string;
  businessUnitVolumeL: number;
  onSaveNotes?: (skuId: string, notes: string) => Promise<void>;
  // Brand-side: flip a recipe line's visibility on the batch sheet (keeps it in the recipe). When
  // omitted (manufacturer), hidden lines are simply filtered out and there are no toggles.
  onToggleLine?: (skuId: string, ingredient: string, hide: boolean) => Promise<void>;
}) {
  // Only SKUs that actually have a recipe can be batched (no gummies / mix packs).
  const withRecipe = useMemo(
    () => skus.filter((s) => !s.discontinued && (recipes[skuRecipeKey(s)] || []).length > 0),
    [skus, recipes],
  );
  // Default to a spirit/bottle if there is one, else the first with a recipe.
  const [skuId, setSkuId] = useState(
    () => (withRecipe.find((s) => s.productType === "bottle") || withRecipe[0])?.id || "",
  );
  const [mode, setMode] = useState<Mode>("bbl");
  const [amount, setAmount] = useState("18");

  const sku = withRecipe.find((s) => s.id === skuId) || null;
  const lines = sku ? recipes[skuRecipeKey(sku)] || [] : [];

  // Standing batch notes, synced to the selected SKU. Editable + saved when onSaveNotes is provided.
  const [notes, setNotes] = useState(sku?.batchNotes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editing, setEditing] = useState(false);
  useEffect(() => { setNotes(sku?.batchNotes || ""); setEditing(false); setEditingLines(false); }, [skuId, sku?.batchNotes]);
  const notesDirty = notes.trim() !== (sku?.batchNotes || "").trim();
  const saveNotes = async () => {
    if (!sku || !onSaveNotes || !notesDirty) { setEditing(false); return; }
    setSavingNotes(true);
    try { await onSaveNotes(sku.id, notes.trim()); setEditing(false); }
    finally { setSavingNotes(false); }
  };

  const lPerGal = Number(conv.gallonL) || 3.78541;
  const galPerBbl = Number(conv.barrelGallon) || 31;
  const gPerLb = gramsPerMassUnit(massUnit);          // 453.592 (lb) or 1000 (kg)
  const lPerBbl = lPerGal * galPerBbl;                 // ≈117.35 L per barrel
  // The volume of ONE finished unit, for the bottle/can count: the SKU's own net volume if set, else the
  // business unit volume (a can). Spirits should carry volumeMl = 750.
  const unitVolL = (sku?.volumeMl && sku.volumeMl > 0 ? sku.volumeMl / 1000 : 0) || businessUnitVolumeL || 0.355;

  // Convert whatever the user typed into barrels, then into total litres.
  const n = Number(amount) || 0;
  const bbl = mode === "bbl" ? n : mode === "litres" ? n / lPerBbl : (n * unitVolL) / lPerBbl;
  const litres = bbl * lPerBbl;
  const gallons = bbl * galPerBbl;
  const units = unitVolL ? litres / unitVolL : 0;

  // Switching unit keeps the SAME batch — convert the entered number to the equivalent in the new unit
  // (18 BBL → ~2112.26 L → ~2816 bottles) instead of leaving it at 18.
  const changeMode = (m: Mode) => {
    if (m === mode) return;
    const equiv = m === "bbl" ? bbl : m === "litres" ? litres : units;
    const rounded = m === "bottles" ? Math.round(equiv) : Math.round(equiv * 10000) / 10000;
    setAmount(equiv ? String(rounded) : "");
    setMode(m);
  };

  const canToggle = !!onToggleLine;
  const [editingLines, setEditingLines] = useState(false);
  const [togglingLine, setTogglingLine] = useState<string | null>(null);
  const showChecks = canToggle && editingLines; // interactive checkboxes only in edit mode
  const toggleLine = async (ingredient: string, hide: boolean) => {
    if (!sku || !onToggleLine) return;
    setTogglingLine(ingredient);
    try { await onToggleLine(sku.id, ingredient, hide); }
    finally { setTogglingLine(null); }
  };

  // g/L for each ingredient (stored directly, or recovered from lbs-per-unit ÷ the business unit volume).
  const allRows = lines.map((l) => {
    const gL = l.gPerL != null && l.gPerL > 0
      ? l.gPerL
      : businessUnitVolumeL > 0 ? ((Number(l.lbsPerCan) || 0) * gPerLb) / businessUnitVolumeL : 0;
    const grams = gL * litres;
    return {
      ingredient: l.ingredient,
      hidden: !!l.hideFromBatch,
      gL,
      lbsPerBbl: (gL / gPerLb) * lPerBbl,   // reference: mass units per barrel
      grams,
      lbs: grams / gPerLb,                  // total in the pricing mass unit (lb or kg)
      kg: grams / 1000,
    };
  });
  // Manufacturer (and the print/PDF) only ever see visible lines. The brand sees all rows so it can
  // toggle them; hidden rows are dimmed and excluded from the totals.
  const displayRows = canToggle ? allRows : allRows.filter((r) => !r.hidden);
  const rows = allRows.filter((r) => !r.hidden); // what actually prints
  const mu = (massUnit || "lb").toLowerCase() === "kg" ? "kg" : "lb";
  const muCap = mu === "kg" ? "Kg" : "Lbs";
  const unitWord = unitLabel === "can" || !unitLabel ? "bottles/cans" : `${unitLabel}s`;
  const unitsLabel = unitWord === "bottles/cans" ? "Bottles" : unitWord.charAt(0).toUpperCase() + unitWord.slice(1);

  // Print / Save-as-PDF: open a clean, self-contained sheet and trigger the browser print dialog.
  const printSheet = () => {
    if (!sku) return;
    const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c] as string));
    const rowsHtml = rows.map((r) => `<tr><td>${esc(r.ingredient)}</td><td class="n b">${fx(r.lbs, 5)}</td></tr>`).join("");
    const header = [["BBL", fx(bbl, 2)], ["US gallons", fx(gallons, 2)], ["Litres", fx(litres, 5)], [unitsLabel, fx(units, 2)]]
      .map(([k, v]) => `<div class=hc><div class=hk>${k}</div><div class=hv>${v}</div></div>`).join("");
    const notesHtml = notes.trim() ? `<h2>Special batch notes</h2><pre class=notes>${esc(notes.trim())}</pre>` : "";
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Batch sheet — ${esc(sku.name)}</title>
<style>
/* margin:0 on the page, real whitespace comes from the body padding below — so side & top
   margins survive even if the print dialog's margin is set to "None". */
@page{size:A4 portrait;margin:0}
*{box-sizing:border-box}
html,body{height:auto}
body{font:12px/1.4 -apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#111;margin:0;padding:14mm}
/* Whole sheet is one block that the browser must not split across pages. */
.sheet{page-break-inside:avoid;break-inside:avoid}
h1{font-size:18px;margin:0 0 2px}.sub{color:#666;margin:0 0 12px;font-size:11px}
.hdr{display:flex;gap:8px;margin:0 0 12px}.hc{border:1px solid #ddd;border-radius:8px;padding:6px 10px;min-width:90px}
.hk{font-size:9px;text-transform:uppercase;letter-spacing:.04em;color:#888}.hv{font-size:16px;font-weight:600}
table{width:100%;border-collapse:collapse;margin:0 0 6px}th,td{padding:4px 8px;border-bottom:1px solid #e5e5e5;text-align:left}
tr{page-break-inside:avoid;break-inside:avoid}
th{font-size:9px;text-transform:uppercase;letter-spacing:.04em;color:#666;border-bottom:1px solid #bbb}
td.n,th.n{text-align:right;font-variant-numeric:tabular-nums}td.b{font-weight:700}
tfoot td{border-top:2px solid #333;font-weight:700}
h2{font-size:12px;margin:14px 0 4px}.notes{white-space:pre-wrap;font:inherit;font-size:11px;border:1px solid #eee;border-radius:8px;padding:8px 10px;background:#fafafa;margin:0}
.foot{margin-top:14px;color:#999;font-size:10px}
@media print{.hc,.notes{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
<div class="sheet">
<h1>${esc(sku.name)} — Batch sheet</h1>
<p class=sub>Batch size: ${fx(bbl, 2)} BBL &middot; ${fx(litres, 5)} L &middot; ${fx(units, 2)} ${esc(unitsLabel.toLowerCase())}</p>
<div class=hdr>${header}</div>
<table><thead><tr><th>Ingredient</th><th class=n>${muCap} needed</th></tr></thead>
<tbody>${rowsHtml}</tbody></table>
${notesHtml}
<p class=foot>${muCap} needed = each ingredient's recipe &times; the batch size above. Generated from the production tool.</p>
</div>
<script>
// Shrink-to-fit: if the sheet is taller than one printable A4 page (A4 297mm minus the 28mm top+bottom
// padding), scale it down so everything stays on ONE sheet.
(function(){
  var mm=3.7795, pageH=(297-28)*mm, el=document.querySelector('.sheet');
  function fit(){ if(!el) return; el.style.transform=''; el.style.transformOrigin='top left';
    var h=el.getBoundingClientRect().height; if(h>pageH){ var s=Math.max(0.5,pageH/h); el.style.transform='scale('+s+')'; } }
  fit(); window.addEventListener('beforeprint',fit);
})();
</script>
</body></html>`;
    const w = window.open("", "_blank", "width=860,height=1100");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.setTimeout(() => w.print(), 300);
  };

  if (!withRecipe.length) {
    return <EmptyState title="No recipes to batch yet" sub="Add a recipe to a SKU (in Settings) and it will appear here to scale up." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">Batch sheet</h3>
        <button className="btn-sm border border-edge hover:bg-paper inline-flex items-center gap-1.5" onClick={printSheet} disabled={!sku}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V2h12v7"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
          Print / Save PDF
        </button>
      </div>
      <div className="card space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <label className="flex-1 min-w-0">
            <span className="label">Product</span>
            <select className="input" value={skuId} onChange={(e) => setSkuId(e.target.value)}>
              {withRecipe.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <div className="shrink-0">
            <span className="label">Batch size in</span>
            <SegTabs
              tabs={[{ id: "bbl", label: "BBL" }, { id: "bottles", label: unitWord === "bottles/cans" ? "Bottles" : unitWord }, { id: "litres", label: "Litres" }]}
              value={mode} onChange={(m) => changeMode(m as Mode)}
            />
          </div>
          <label className="shrink-0 w-32">
            <span className="label">{mode === "bbl" ? "Barrels" : mode === "litres" ? "Litres" : "Units"}</span>
            <input type="number" step="any" min="0" className="input text-right tabular-nums text-lg font-semibold" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
        </div>

        {/* Header conversions — BBL → Gallons → Litres → Bottles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { k: "BBL", v: fx(bbl, 2) },
            { k: "US gallons", v: fx(gallons, 2) },
            { k: "Litres", v: fx(litres, 5) },
            { k: unitsLabel, v: fx(units, 2) },
          ].map((c, i) => (
            <div key={c.k} className={`rounded-xl border border-edge/70 px-3 py-2 ${i === 3 ? "bg-brand/[0.06] border-brand/25" : "bg-paper/60"}`}>
              <div className="text-[11px] uppercase tracking-wide text-muted">{c.k}</div>
              <div className="text-lg font-semibold tabular-nums text-ink">{c.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Ingredient breakdown — deliberately simple: what to weigh out, per ingredient. */}
      <div className="card p-0 overflow-hidden">
        {canToggle && (
          <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-edge/60">
            <span className="text-xs font-medium text-muted">{editingLines ? "Tick the lines to show on the batch sheet" : "Ingredients"}</span>
            <button className={`btn-sm ${editingLines ? "btn-primary" : "border border-edge hover:bg-paper"}`} onClick={() => setEditingLines((v) => !v)}>{editingLines ? "Done" : "Edit lines"}</button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-edge/70">
              {showChecks && <th className="th text-center w-10" title="Show on batch sheet">Show</th>}
              <th className="th text-left">Ingredient</th>
              <th className="th text-right">{muCap} needed</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((r) => (
              <tr key={r.ingredient} className={`border-b border-edge/40 last:border-0 ${r.hidden ? "opacity-45" : ""}`}>
                {showChecks && (
                  <td className="td text-center">
                    <input
                      type="checkbox"
                      className="align-middle cursor-pointer w-4 h-4"
                      checked={!r.hidden}
                      disabled={togglingLine === r.ingredient}
                      onChange={(e) => toggleLine(r.ingredient, !e.target.checked)}
                      title={r.hidden ? "Hidden from the batch sheet — check to show" : "Shown — uncheck to hide (stays in the recipe)"}
                    />
                  </td>
                )}
                <td className="td font-medium text-ink">{r.ingredient}{r.hidden && <span className="ml-2 text-[10px] uppercase tracking-wide text-muted">hidden</span>}</td>
                <td className="td text-right tabular-nums font-semibold text-ink whitespace-nowrap">{fx(r.lbs, 5)} <span className="text-muted font-normal text-xs">{mu}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted px-1">
        {canToggle
          ? <span>{muCap} needed = each ingredient's recipe × the batch size above. Use <b>Edit lines</b> to hide a line (e.g. THC) — it stays in the recipe for ordering &amp; COGS.</span>
          : <span>{muCap} needed = each ingredient's recipe × the batch size above.</span>}
      </p>

      {/* Special batch notes — standing per-product instructions (deaeration, carbonation, dose, PH…).
          Editable (with an Edit button) on the brand side; view-only when onSaveNotes isn't provided. */}
      <div className="card space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold">Special batch notes {sku && <span className="text-muted font-normal">· {sku.name}</span>}</h4>
          {onSaveNotes && !editing && (
            <button className="btn-sm border border-edge hover:bg-paper" onClick={() => setEditing(true)}>Edit</button>
          )}
          {onSaveNotes && editing && (
            <div className="flex gap-2">
              <button className="btn-ghost btn-sm" disabled={savingNotes} onClick={() => { setNotes(sku?.batchNotes || ""); setEditing(false); }}>Cancel</button>
              <button className="btn-primary btn-sm" disabled={savingNotes} onClick={saveNotes}>{savingNotes ? "Saving…" : "Save"}</button>
            </div>
          )}
        </div>
        {editing ? (
          <textarea
            className="input min-h-[104px] resize-y leading-relaxed"
            placeholder={"e.g.\n• Deaerate before adding any juices / purées\n• Non-carbonated · Non-pasteurised (using preservatives)\n• 200mg THC · No CBD\n• Please test PH"}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            autoFocus
          />
        ) : notes ? (
          <p className="text-sm whitespace-pre-wrap text-ink/90">{notes}</p>
        ) : (
          <p className="text-sm text-muted">No batch notes for this product yet.</p>
        )}
        {onSaveNotes && editing && <p className="text-[11px] text-muted">These notes save against the product — the manufacturer sees them (read-only) on their batch sheet.</p>}
      </div>
    </div>
  );
}
